import { getExt } from 'Cloud/Application/Editor/helpers';
import { EditorID } from 'Cloud/Application/Editor/types';
import { extInfo } from 'lib/extInfo';
import React, { useEffect, useState } from 'react';
import { getSharedAttachUrl } from 'reactApp/api/axios.corsapi';
import { MYOFFICE_ATTACHES_VIEWER, VIEWERS_CONFIG } from 'reactApp/appHelpers/configHelpers';
import { R7_FOR_SHARING_ATTACHES } from 'reactApp/appHelpers/featuresHelpers/features/r7ForSharingAttach';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { getItemNameWithoutExt } from 'reactApp/modules/file/utils';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { EStatus } from 'reactApp/sections/ErrorPage/ErrorPage.types';
import { ErrorPageBlock } from 'reactApp/sections/ErrorPage/ErrorPageBlock';
import { ReactViewerLetters } from 'reactApp/ui/ReactViewer/ReactViewerLetters';
import { noopVoid } from 'reactApp/utils/helpers';

const XRAY_CONFIG = {
    defaultParams: { p: 'sharing_attach_viewer' },
};

function encodeFileParam(url: string) {
    return url?.replace(/(file=)([^&]+)/, (_, prefix, filename) => {
        return prefix + encodeURIComponent(filename);
    });
}

const getFile = (id: string, fileName: string, viewUrl: string, downloadUrl: string) => {
    const ext = getExt(fileName);
    const { kind, subKind } = extInfo.get(ext);

    return {
        ext,
        kind,
        subKind,
        isFolder: false,
        id,
        name: fileName,
        mtime: '',
        nameWithoutExt: fileName ? getItemNameWithoutExt(fileName, ext) : '',
        parent: '',
        size: 0,
        storage: EStorageType.embedded,
        thumbnails: {
            original: viewUrl,
        },
        url: {
            get: downloadUrl,
            view: viewUrl,
            download: R7_FOR_SHARING_ATTACHES ? encodeFileParam(downloadUrl) : downloadUrl,
        },
        // легаси параметр, для r7 не требуется, но является обязательным
        content_type: 'r7content',
    };
};

const r7Viewer = VIEWERS_CONFIG.find(({ id }) => id === EditorID.R7);

interface SharingAttachQueryParams {
    file: string;
    attach_id: string;
    autogen_token: string;
    autogen_secret_key: string;
}

export const SharingAttachPage = () => {
    const { attach_id, file: fileName, autogen_token, autogen_secret_key } = getQueryParams() as Partial<SharingAttachQueryParams>;

    const [file, setFile] = useState<any | null>(null);

    useEffect(() => {
        if (!attach_id || !fileName || !autogen_token || !autogen_secret_key) {
            return;
        }

        const viewUrl = getSharedAttachUrl({ id: attach_id, fileName, autogen_token, autogen_secret_key });
        const downloadUrl = getSharedAttachUrl({ id: attach_id, fileName, autogen_token, autogen_secret_key, notype: '1' });

        setFile(getFile(attach_id, fileName, viewUrl, downloadUrl));
    }, [attach_id, fileName, autogen_token, autogen_secret_key]);

    if (!attach_id || !fileName || !autogen_token || !autogen_secret_key) {
        return <ErrorPageBlock status={EStatus.NOT_FOUND} />;
    }

    return (
        <ReactViewerLetters
            deleteFile={noopVoid}
            file={file}
            isActionOpen={false}
            isAuthorized={false}
            isMountedFolder={false}
            isPublicCopyEditable={false}
            itemCount={1}
            isNewbie={true}
            onClose={noopVoid}
            publishItemAction={noopVoid}
            selectItemIndexInArray={noopVoid}
            showEditorHeader={false}
            showSnackbar={noopVoid}
            showViewerHeader={true}
            isViewer={true}
            viewers={R7_FOR_SHARING_ATTACHES && r7Viewer ? [r7Viewer] : [MYOFFICE_ATTACHES_VIEWER]}
            toggleFavorite={noopVoid}
            sendSearchRadar={noopVoid}
            xrayConfig={XRAY_CONFIG}
            isViewerFileVerifiedByKaspersky={false}
            gaCategory="sharing_attach"
            itemStorage={EStorageType.viewerAttaches}
            sharingAttach
        />
    );
};
